/** @jsxImportSource @emotion/react */
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import {
  DataTableTextCells,
  OrderType,
  orderTypeLabel,
  StatisticField,
} from '../../constants';
import {
  DataFiltersModel,
  IPlainObject,
  PaginationBaseModel,
} from '../../models';
import { cn } from '../../utils';
import { formatCellByType } from '../../utils/cell-formatters';
import React, { MouseEventHandler, ReactElement, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CustomPagination } from '../pagination/Pagination';
import { useDataTableStyles } from './DataTable.styles';
import { DataColumnModel } from './models/DataColumnModel';

interface CountryBaseModel {
  code: string;
  id?: string;
}

interface DataTableProps<T> {
  data: T[];
  idField?: keyof T;
  size?: 'small' | 'medium';
  columns: DataColumnModel<T>[];
  customRender?: IPlainObject;
  filters?: PaginationBaseModel;
  loading?: boolean;
  readOnly?: boolean;
  minWidth?: number;
  width?: number;
  onFiltersChange?: (filters: DataFiltersModel) => void;
  onRowClick?: (item: T, event: MouseEventHandler<HTMLTableRowElement>) => void;
  actionWidth?: string;
  actionRender?: (item: T) => ReactElement;
}

type directionType = 'asc' | 'desc';

export function DataTable<T extends IPlainObject | CountryBaseModel>({
  data,
  idField,
  size,
  columns,
  filters,
  loading = false,
  readOnly = false,
  minWidth,
  width: tableWidth,
  onFiltersChange = () => null,
  onRowClick,
  actionWidth,
  actionRender,
  customRender = {},
}: DataTableProps<T>): ReactElement {
  const styles = useDataTableStyles();

  const { total, ...dataFilters } = filters || ({} as PaginationBaseModel);
  const sort = ((filters &&
    filters.orderColumn &&
    orderTypeLabel[filters.orderType]) ||
    null) as directionType;

  const gridColumns = useMemo(
    () =>
      columns.filter(
        (item) =>
          (filters && filters.oneDay) ||
          item.field !== StatisticField.StatUniqCnt,
      ),
    [columns, filters && filters.oneDay],
  );

  const sortColumn = (field: string, defaultSort?: OrderType): void => {
    if (!filters) {
      return;
    }

    if (filters.orderColumn !== field) {
      return onFiltersChange({
        ...dataFilters,
        orderColumn: field,
        orderType: OrderType.Asc,
      });
    }

    if (filters.orderType === OrderType.Asc) {
      return onFiltersChange({
        ...dataFilters,
        orderColumn: field,
        orderType: OrderType.Desc,
      });
    }

    if (defaultSort && filters.orderType === defaultSort) {
      return onFiltersChange({
        ...dataFilters,
        orderColumn: field,
        orderType:
          defaultSort === OrderType.Desc ? OrderType.Asc : OrderType.Desc,
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { orderColumn, orderType, ...others } = dataFilters;

    return onFiltersChange(others);
  };

  const onPageChange = (page) => {
    onFiltersChange({
      ...dataFilters,
      page,
    });
  };

  const handleChangeRowsPerPage = (e) => {
    onFiltersChange({
      ...dataFilters,
      page: 0,
      perPage: e.target.value,
    });
  };

  const renderCellWithLink = (
    item: T,
    link: (rowItem: T) => string,
    targetBlank: boolean,
    cell: ReactElement,
  ): ReactElement => {
    if (!link || readOnly) {
      return cell;
    }

    const href = link(item);

    if (!href) {
      return cell;
    }

    if (targetBlank) {
      return (
        <Link target="_blank" to={href}>
          {cell}
        </Link>
      );
    }

    return <Link to={href}>{cell}</Link>;
  };

  const getTableHead = (): ReactElement[] =>
    gridColumns.map(
      ({
        field,
        headerName,
        headerTip,
        type,
        width,
        noSort,
        defaultSort,
      }: DataColumnModel<T>) => (
        <TableCell
          key={field}
          align={!type || DataTableTextCells[type] ? 'left' : 'right'}
          style={{ width: width || '20%' }}
        >
          {noSort ? (
            <span>
              {headerName}{' '}
              {headerTip && <span className="header-tip">{headerTip}</span>}
            </span>
          ) : (
            <TableSortLabel
              active={filters && filters.orderColumn === field}
              direction={sort || 'asc'}
              onClick={() => sortColumn(field, defaultSort)}
            >
              <span>
                {headerName}{' '}
                {headerTip && <span className="header-tip">{headerTip}</span>}
              </span>
            </TableSortLabel>
          )}
        </TableCell>
      ),
    );

  const renderTableRow = (item): ReactElement[] =>
    gridColumns.map(({ render, ...columnProps }: DataColumnModel<T>) => {
      const { field, type, main, link, targetBlank } = columnProps;
      const fomattedValue = formatCellByType<T>(item, field, type);
      const cellContent = (render && render(item, columnProps)) || (
        <div className="table-cell" title={fomattedValue}>
          {fomattedValue}
        </div>
      );

      const content = customRender[field]
        ? customRender[field](item)
        : renderCellWithLink(
            item,
            link,
            targetBlank,
            main ? (
              <Typography color="textPrimary" variant="subtitle1">
                {cellContent}
              </Typography>
            ) : (
              <>{cellContent}</>
            ),
          );

      return (
        <TableCell
          key={field}
          align={!type || DataTableTextCells[type] ? 'left' : 'right'}
          style={{ width: tableWidth || '20%', overflow: 'hidden' }}
        >
          {content}
        </TableCell>
      );
    });

  const getTableBody = (): ReactElement[] =>
    data.map((item) => (
      <TableRow
        onClick={(e: unknown) =>
          onRowClick &&
          onRowClick(item, e as MouseEventHandler<HTMLTableRowElement>)
        }
        hover
        key={(item[idField] as string) || item.id || item.code}
        className={cn({ clickable: !!onRowClick })}
      >
        {renderTableRow(item)}
        {actionRender ? (
          <TableCell align={'right'} style={{ width: actionWidth || '125px' }}>
            {actionRender(item)}
          </TableCell>
        ) : null}
      </TableRow>
    ));

  const pagination = (): ReactElement =>
    filters && (
      <Box className="pagination" display="flex" justifyContent="flex-end">
        <CustomPagination
          total={total}
          page={filters.page || 0}
          perPage={filters.perPage}
          onChange={onPageChange}
          disabled={loading}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Box>
    );

  return (
    <div css={styles}>
      <div className="grid-container">
        <Box minWidth={minWidth || 800}>
          <Table
            size={size}
            className="data-table"
            style={tableWidth ? { minWidth: `${tableWidth}px` } : {}}
          >
            <TableHead>
              <TableRow>
                {getTableHead()}
                {actionRender ? (
                  <TableCell
                    align={'right'}
                    style={{ width: actionWidth || '125px' }}
                  >
                    Actions
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>{getTableBody()}</TableBody>
          </Table>
        </Box>
      </div>
      {data && !!data.length ? (
        pagination()
      ) : (
        <div className="pagination">
          <Typography color="textPrimary" variant="subtitle1">
            No data found
          </Typography>
        </div>
      )}
    </div>
  );
}
